import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { usePermisos } from '../hooks/usePermisos';
import { MenuPageIntro, HeaderIOS, SeccionApps, TarjetaApp, IOS } from './shared/MenuLayout';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, Tooltip as CTooltip, Filler
} from 'chart.js';
import api from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, CTooltip, Filler);

const MID = 3;

const OPCIONES = [
  {id:'ventas',        n:'Ventas',         r:'/caja/ventas'},
  {id:'anulaciones',   n:'Anulaciones',    r:'/caja/anulaciones'},
  {id:'tarifario',     n:'Tarifario',      r:'/caja/tarifario'},
  {id:'sesiones',      n:'Sesiones',       r:'/caja/sesiones'},
  {id:'procedimientos',n:'Procedimientos', r:'/caja/procedimientos'},
  {id:'reporte',       n:'Reportes',       r:'/caja/reportes'},
  {id:'ajustes',       n:'Ajustes',        r:'/caja/ajustes'},
];

const REQUIEREN_SESION = ['ventas'];

// ── Mini gráfico sesiones ─────────────────────────────────────────────────────
const GraficoSesiones = ({ data }) => {
  const labels = data.map(s=>s.fecha);
  const ventas = data.map(s=>s.totalVentas||0);
  const chart = {
    labels,
    datasets:[{
      label:'Ventas',
      data: ventas,
      borderColor: IOS.green,
      backgroundColor: ctx=>{
        if(!ctx.chart.chartArea) return 'rgba(45,158,107,0.08)';
        const {top,bottom} = ctx.chart.chartArea;
        const g = ctx.chart.ctx.createLinearGradient(0,top,0,bottom);
        g.addColorStop(0, `${IOS.green}22`);
        g.addColorStop(1, `${IOS.green}00`);
        return g;
      },
      tension:0.45, fill:true,
      pointRadius:3, pointHoverRadius:6,
      pointBackgroundColor:IOS.green,
      pointBorderColor:'white', pointBorderWidth:2,
      borderWidth:2,
    }]
  };
  const opts = {
    responsive:true, maintainAspectRatio:false,
    animation:{duration:900,easing:'easeOutQuart'},
    interaction:{ mode:'nearest', intersect:true },
    plugins:{
      datalabels:{ display:false },
      legend:{display:false},
      tooltip:{
        backgroundColor:'rgba(255,255,255,0.97)',
        borderColor:'rgba(0,0,0,0.06)', borderWidth:1,
        titleColor:IOS.label2, bodyColor:IOS.label,
        padding:8, cornerRadius:8, displayColors:false,
        callbacks:{
          title: items => items[0].label,
          label: ctx =>`S/ ${ctx.parsed.y.toLocaleString('es-PE',{minimumFractionDigits:2})}`,
          labelColor: ()=>({ borderColor:IOS.green, backgroundColor:IOS.green, borderRadius:4 }),
        }
      }
    },
    scales:{
      x:{ grid:{display:false}, border:{display:false}, ticks:{color:'rgba(60,60,67,0.4)',font:{size:9}} },
      y:{ display:false, beginAtZero:true }
    }
  };
  return <Line data={chart} options={opts}/>;
};

// ── Mini gráfico anulaciones ──────────────────────────────────────────────────
const GraficoAnulaciones = ({ data }) => {
  const labels = data.map(s=>s.mes);
  const vals   = data.map(s=>s.total||0);
  const chart  = {
    labels,
    datasets:[{
      label:'Anulaciones',
      data: vals,
      backgroundColor: ctx => {
        const v = vals[ctx.dataIndex]||0;
        const max = Math.max(...vals,1);
        const ratio = v/max;
        return ratio>0.7?`${IOS.red}CC`:ratio>0.4?`${IOS.orange}CC`:`${IOS.green}CC`;
      },
      borderRadius:6, borderSkipped:false,
    }]
  };
  const opts = {
    responsive:true, maintainAspectRatio:false,
    animation:{duration:800,easing:'easeOutQuart'},
    plugins:{
      datalabels:{ display:false },
      legend:{display:false},
      tooltip:{
        backgroundColor:'rgba(255,255,255,0.96)',
        borderColor:IOS.separator, borderWidth:1,
        titleColor:IOS.label, bodyColor:IOS.label,
        padding:8, cornerRadius:8,
      }
    },
    scales:{
      x:{grid:{display:false},border:{display:false},ticks:{font:{size:9},color:IOS.label2}},
      y:{grid:{color:'rgba(0,0,0,0.04)'},border:{display:false},
        ticks:{font:{size:9},color:IOS.label2,stepSize:1},beginAtZero:true},
    }
  };
  return <Bar data={chart} options={opts}/>;
};

// ── Modal Apertura ────────────────────────────────────────────────────────────
const ModalApertura = ({ open, onClose, onAbierta, idUe }) => {
  const [monto,     setMonto]     = useState('');
  const [abriendo,  setAbriendo]  = useState(false);
  const [error,     setError]     = useState('');
  const [cajas,     setCajas]     = useState([]);
  const [cajaSel,   setCajaSel]   = useState(null);
  const [cargCajas, setCargCajas] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMonto(''); setError(''); setCajaSel(null); setConfirmando(false);
    setCargCajas(true);
    // ↓ CAJA_LISTAR_CAJAS_VENTAS — todas las cajas de la UE
    api.get('/caja/cajas-ventas', { params: { idUe } })
      .then(r => {
        if (r.data.success && r.data.data?.length > 0) {
          setCajas(r.data.data);
          // Sin preselección: el cajero debe elegir su caja conscientemente
        } else {
          setCajas([]); setCajaSel(null);
          setError('No hay cajas disponibles');
        }
      })
      .catch(() => setError('Error al cargar cajas'))
      .finally(() => setCargCajas(false));
  }, [open, idUe]);

  const abrir = async () => {
    if (!monto || parseFloat(monto) < 0) { setError('Ingresa el monto base'); return; }
    if (!cajaSel)   { setError('Selecciona una caja'); return; }
    if (cajaSel.ocupada) { setError('Esta caja está en uso. Elige otra.'); return; }
    if (!confirmando) { setConfirmando(true); setError(''); return; }
    setAbriendo(true); setError('');
    try {
      const r = await api.post('/caja/abrir', {
        idCaja:    cajaSel.idCaja,
        baseMonto: parseFloat(monto),
        nombrePc:  navigator.platform || 'WEB',
      });
      if (r.data.success) { onAbierta(); onClose(); }
      else setError(r.data.message || 'Error al abrir caja');
    } catch(e) { setError('Error de conexión'); }
    finally { setAbriendo(false); }
  };

  if (!open) return null;
  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.5)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center',
      backdropFilter:'blur(6px)', px:2 }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:'20px', width:'100%', maxWidth:400,
        boxShadow:'0 24px 60px rgba(0,0,0,0.25)', overflow:'hidden' }}>

        {/* Header */}
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <Box sx={{ width:30, height:30, borderRadius:'9px', bgcolor:`${IOS.green}12`,
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="7" width="20" height="14" rx="2"/>
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              </svg>
            </Box>
            <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>Apertura de caja</Typography>
          </Box>
          <Box component="button" onClick={onClose}
            sx={{ width:26, height:26, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </Box>
        </Box>

        <Box sx={{ p:2.5, display:'flex', flexDirection:'column', gap:1.5 }}>

          {/* Selector de cajas */}
          {cargCajas
            ? <Box sx={{ py:2, display:'flex', alignItems:'center', justifyContent:'center', gap:1 }}>
                <CircularProgress size={14} sx={{ color:IOS.green }}/>
                <Typography sx={{ fontSize:'0.78rem', color:IOS.label2 }}>Cargando cajas...</Typography>
              </Box>
            : <>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                    textTransform:'uppercase', letterSpacing:'0.07em', mb:0.8 }}>
                    Selecciona la caja donde vas a trabajar
                  </Typography>
                  <Box sx={{ display:'flex', flexDirection:'column', gap:0.6,
                    maxHeight:220, overflowY:'auto',
                    '&::-webkit-scrollbar':{ width:4 },
                    '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                    {cajas.map(c => {
                      const selec   = cajaSel?.idCaja === c.idCaja;
                      const ocupada = c.ocupada;
                      return (
                        <Box key={c.idCaja}
                          onClick={() => { if(!ocupada){ setCajaSel(c); setConfirmando(false); } }}
                          sx={{ display:'flex', alignItems:'center', gap:1.2, p:1.3,
                            borderRadius:'12px', cursor:ocupada?'not-allowed':'pointer',
                            transition:'all 0.15s', opacity:ocupada?0.55:1,
                            border:`1.5px solid ${selec?IOS.green:IOS.separator}`,
                            bgcolor:selec?`${IOS.green}08`:ocupada?IOS.bg:IOS.surface,
                            '&:hover':{ borderColor:ocupada?IOS.separator:`${IOS.green}60`,
                              bgcolor:ocupada?IOS.bg:`${IOS.green}05` } }}>

                          {/* Radio */}
                          <Box sx={{ width:18, height:18, borderRadius:'50%', flexShrink:0,
                            bgcolor:selec?IOS.green:'transparent',
                            border:`2px solid ${selec?IOS.green:ocupada?IOS.separator:IOS.label2}`,
                            display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {selec && (
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                                stroke="white" strokeWidth="3.5" strokeLinecap="round">
                                <path d="M20 6L9 17l-5-5"/>
                              </svg>
                            )}
                          </Box>

                          {/* Info caja */}
                          <Box sx={{ flex:1, minWidth:0 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                              <Typography sx={{ fontSize:'0.85rem',
                                fontWeight:selec?700:500,
                                color:selec?IOS.green:ocupada?IOS.label2:IOS.label }}>
                                {c.nombre || `Caja #${c.idCaja}`}
                              </Typography>
                              {ocupada && (
                                <Box sx={{ px:0.6, py:0.1, borderRadius:'6px',
                                  bgcolor:`${IOS.red}10`, border:`1px solid ${IOS.red}20` }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.red }}>
                                    EN USO
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                              <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontFamily:'monospace' }}>
                                Serie: <strong>{c.serieElectronica || '—'}</strong>
                              </Typography>
                              {ocupada && c.ocupadaPor && (
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.red }}>
                                  · {c.ocupadaPor}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Icono estado */}
                          <Box sx={{ width:28, height:28, borderRadius:'8px', flexShrink:0,
                            bgcolor:ocupada?`${IOS.red}10`:selec?`${IOS.green}15`:`${IOS.label2}08`,
                            display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {ocupada
                              ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                  stroke={IOS.red} strokeWidth="2" strokeLinecap="round">
                                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                              : <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                  stroke={selec?IOS.green:IOS.label2} strokeWidth="2" strokeLinecap="round">
                                  <rect x="2" y="7" width="20" height="14" rx="2"/>
                                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                                </svg>
                            }
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Monto base */}
                {cajaSel && !cajaSel.ocupada && (
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                      textTransform:'uppercase', letterSpacing:'0.07em', mb:0.5 }}>
                      Monto base *
                    </Typography>
                    <Box sx={{ position:'relative' }}>
                      <Box sx={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)',
                        fontSize:'0.9rem', fontWeight:700, color:IOS.label2, pointerEvents:'none' }}>S/</Box>
                      <input type="number" min="0" step="0.01" placeholder="0.00"
                        value={monto} onChange={e => { setMonto(e.target.value); setConfirmando(false); }} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter' && !confirmando) abrir(); }}
                        style={{ width:'100%', height:48, paddingLeft:30, paddingRight:12,
                          border:`2px solid ${IOS.green}`, borderRadius:12, fontSize:'1.2rem',
                          fontWeight:700, color:IOS.label, outline:'none',
                          backgroundColor:IOS.surface, fontFamily:'monospace', boxSizing:'border-box' }}/>
                    </Box>
                  </Box>
                )}

                {/* Info equipo */}
                <Box sx={{ p:1.2, bgcolor:IOS.bg, borderRadius:'10px',
                  border:`1px solid ${IOS.separator}`, display:'flex', gap:2 }}>
                  {[
     
                    ['Fecha',  new Date().toLocaleDateString('es-PE')],
                    ['Hora',   new Date().toLocaleTimeString('es-PE', { hour:'2-digit', minute:'2-digit' })]
                  ].map(([l, v]) => (
                    <Box key={l}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                        textTransform:'uppercase', letterSpacing:'0.06em', mb:0.2 }}>{l}</Typography>
                      <Typography sx={{ fontSize:'0.75rem', fontWeight:600, color:IOS.label }}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
          }

          {error && (
            <Box sx={{ p:1, borderRadius:'8px', bgcolor:'#FFF0EF', border:'1px solid #FFD4D1' }}>
              <Typography sx={{ fontSize:'0.75rem', color:IOS.red, fontWeight:600 }}>{error}</Typography>
            </Box>
          )}

          {confirmando && cajaSel && (
            <Box sx={{ p:1.5, borderRadius:'12px', bgcolor:`${IOS.orange}08`, border:`1.5px solid ${IOS.orange}40`, textAlign:'center' }}>
              <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mb:0.3 }}>Vas a aperturar</Typography>
              <Typography sx={{ fontSize:'1.05rem', fontWeight:800, color:IOS.orange, lineHeight:1.2 }}>
                {cajaSel.nombre || `Caja #${cajaSel.idCaja}`}
              </Typography>
              <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:IOS.label, mt:0.3, fontFamily:'monospace' }}>
                con S/ {parseFloat(monto||0).toFixed(2)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mt:0.5 }}>
                Verifica que sea la caja que te corresponde hoy
              </Typography>
            </Box>
          )}

          <Box component="button" onClick={abrir}
            disabled={abriendo || !monto || !cajaSel || cajaSel?.ocupada || cargCajas}
            sx={{ width:'100%', height:44, borderRadius:'12px', border:'none',
              bgcolor:abriendo||!monto||!cajaSel||cajaSel?.ocupada||cargCajas?'#E5E5EA':IOS.green,
              cursor:abriendo||!monto||!cajaSel||cajaSel?.ocupada||cargCajas?'default':'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:1,
              boxShadow:monto&&cajaSel&&!cajaSel?.ocupada&&!abriendo?`0 6px 18px ${IOS.green}40`:'none',
              transition:'all 0.2s' }}>
            {abriendo && <CircularProgress size={14} sx={{ color:'white' }}/>}
            <Typography sx={{ fontSize:'0.9rem', fontWeight:700,
              color:abriendo||!monto||!cajaSel||cajaSel?.ocupada||cargCajas?'#8E8E93':'white' }}>
              {abriendo ? 'Abriendo...' : confirmando ? `Sí, abrir ${cajaSel?.nombre || `Caja #${cajaSel?.idCaja}`}` : 'Abrir caja'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Modal sin sesión ──────────────────────────────────────────────────────────
const ModalSinSesion = ({ open, onClose, onAperturar }) => {
  if (!open) return null;
  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.5)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)', px:2 }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:'20px', width:'100%', maxWidth:340,
        boxShadow:'0 24px 60px rgba(0,0,0,0.25)', p:3, textAlign:'center' }}>
        <Box sx={{ width:52, height:52, borderRadius:'50%', bgcolor:`${IOS.orange}12`,
          display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </Box>
        <Typography sx={{ fontWeight:800, fontSize:'1rem', color:IOS.label, mb:0.8 }}>Caja cerrada</Typography>
        <Typography sx={{ color:IOS.label2, fontSize:'0.82rem', lineHeight:1.5, mb:2.5 }}>
          Para acceder a Ventas necesitas tener una sesión de caja activa.
        </Typography>
        <Box sx={{ display:'flex', gap:1, justifyContent:'center' }}>
          <Box component="button" onClick={onClose}
            sx={{ height:36, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
              bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.82rem', fontWeight:600, color:IOS.label2 }}>
            Cancelar
          </Box>
          <Box component="button" onClick={onAperturar}
            sx={{ height:36, px:2, borderRadius:'10px', border:'none', bgcolor:IOS.green,
              cursor:'pointer', '&:hover':{ filter:'brightness(0.92)' } }}>
            <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:'white' }}>Aperturar →</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const Caja = () => {
  const navigate = useNavigate();
  const { tieneOpcion, cargando } = usePermisos();
  const user   = authService.getCurrentUser();
  const nc     = user?.nombreCompleto||user?.usuario||'';
  const cap    = t=>t.toLowerCase().split(' ').map(p=>p.charAt(0).toUpperCase()+p.slice(1)).join(' ');
  const nf     = cap(nc);
  const ueId   = user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0;
  const idEst  = user?.idEstablecimiento||user?.IdEstablecimiento||0;
  const [hov,setHov] = useState(null);
  const logout = ()=>{ authService.logout(); navigate('/login'); };

  const [actividadVista,setActividadVista] = useState('sesiones');
  const [cajaInfo,       setCajaInfo]       = useState(null);
  const [cargCaja,       setCargCaja]       = useState(true);
  const [cerrando,       setCerrando]       = useState(false);
  const [modalApertura,  setModalApertura]  = useState(false);
  const [modalSinSesion, setModalSinSesion] = useState(false);

  // Totales sesión (para la tarjeta control de caja)
  const [totalSesion,    setTotalSesion]    = useState({ suma:0, cantidad:0 });

  const [sesiones,    setSesiones]    = useState([]);
  const [anulaciones, setAnulaciones] = useState([]);
  const [cargGraf,    setCargGraf]    = useState(true);

  const [cpesPendientes, setCpesPendientes] = useState([]);
  const [modalPendientes, setModalPendientes] = useState(false);

  const [enviandoTodos,    setEnviandoTodos]    = useState(false);
  const [reenvioResultado, setReenvioResultado] = useState(null);
  const [enviandoAuto, setEnviandoAuto] = useState(false);

  const reenviarTodos = async () => {
    setEnviandoTodos(true); setReenvioResultado(null);
    try {
      const r = await api.post('/caja/reenviar-pendientes', {
        idUe:      ueId,
        idApertura: cajaInfo?.idApertura || 0,
        idVentas:  cpesPendientes.map(p => p.idVenta),
      });
      if (r.data.success) {
        setReenvioResultado(r.data);
        if (r.data.fallidos === 0) {
          setCpesPendientes([]);
          setTimeout(() => setModalPendientes(false), 1500);
        } else {
          // Recargar solo los que siguen pendientes
          api.get('/caja/cpe-pendientes', { params:{ idUe:ueId, idApertura:cajaInfo?.idApertura } })
            .then(r2=>{ if(r2.data.success) setCpesPendientes(r2.data.data||[]); });
        }
      }
    } catch(e) { setReenvioResultado({ reenviados:0, fallidos: cpesPendientes.length, message:e.response?.data?.message||'No se pudo confirmar el reenvío.' }); }
    finally { setEnviandoTodos(false); }
  };

  const cargarEstado = async () => {
  setCargCaja(true);
  try {
    const r = await api.get('/caja/estado', { params: { idUe: ueId, idEstablecimiento: idEst } });
    if (r.data.success && r.data.abierta) {
      setCajaInfo(r.data.data);
      const idAp  = r.data.data.idApertura;
      // ↓ Usar el ID_ESTABLECIMIENTO de la CAJA (no del usuario)
      const idEstCaja = r.data.data.idEstablecimientoCaja;

      if (idAp) {
        api.get('/caja/dash-total-caja', { params: { idApertura: idAp } })
          .then(r2 => { if (r2.data.success) setTotalSesion(r2.data.data); })
          .catch(() => {});

        // CPEs pendientes
        const r2 = await api.get('/caja/cpe-pendientes', { params: { idUe: ueId, idApertura: idAp } });
        if (r2.data.success && r2.data.data?.length > 0) {
          setCpesPendientes(r2.data.data);
          setEnviandoAuto(true);
          api.post('/caja/reenviar-pendientes', {
            idUe: ueId, idApertura: idAp,
            idVentas: r2.data.data.map(p => p.idVenta)
          }).then(r3 => {
            setReenvioResultado(r3.data);
            if (r3.data.success) {
              if (r3.data.fallidos === 0) setCpesPendientes([]);
              else api.get('/caja/cpe-pendientes', { params: { idUe: ueId, idApertura: idAp } })
                .then(r4 => { if (r4.data.success) setCpesPendientes(r4.data.data || []); });
            }
          }).catch(e => { setReenvioResultado({reenviados:0,fallidos:r2.data.data.length,message:e.response?.data?.message||'No se pudo confirmar el reenvío. Revisa los pendientes antes de reintentar.'}); }).finally(() => setEnviandoAuto(false));
        }
      }
    } else {
      setCajaInfo(null);
      setTotalSesion({ suma: 0, cantidad: 0 });
    }
  } catch(e) { setCajaInfo(null); }
  finally { setCargCaja(false); }
};

  useEffect(()=>{
    if(!ueId) return;
    cargarEstado();
    Promise.all([
      api.get('/caja/ultimas-sesiones',{params:{limite:5}}),
      api.get('/caja/anulaciones-meses'),
    ]).then(([rSes,rAnu])=>{
      if(rSes.data.success) setSesiones(rSes.data.data||[]);
      if(rAnu.data.success) setAnulaciones(rAnu.data.data||[]);
    }).catch(()=>{}).finally(()=>setCargGraf(false));
  },[ueId]);

  const cerrarCaja = () => navigate('/caja/corte');

  const handleOpcion = (o) => {
    if (REQUIEREN_SESION.includes(o.id) && !cajaInfo) {
      setModalSinSesion(true);
      return;
    }
    navigate(o.r);
  };

  const fmtS = n => `S/ ${parseFloat(n||0).toLocaleString('es-PE',{minimumFractionDigits:2})}`;

  return (
    <Box className="integra-menu caja-menu" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
      <HeaderIOS titulo="Caja" subtitulo=""
        onHome={()=>navigate('/dashboard')} onLogout={logout}
        nombreCompleto={nf} avatarLetra={nc.charAt(0).toUpperCase()}/>

      <Box sx={{maxWidth:1400,mx:'auto',px:{xs:2,sm:4},pt:3.5,pb:6}}>
        <Grid container spacing={2.5} className="integra-launcher-layout">

          {/* ── Col izquierda ── */}
          <Grid item xs={12} lg={8}>
        <MenuPageIntro title="Caja" description="Tus operaciones y herramientas de atención, a mano." />
              <Grid container spacing={1.5}>
                {OPCIONES.map(o=>(
                  <Grid item xs={4} sm={2} key={o.id}>
                    <TarjetaApp id={o.id} nombre={o.n} iconKey={o.id}
                      bloqueado={!cargando&&!tieneOpcion(o.id,MID)}
                      hoveredId={hov} onHover={setHov} onLeave={()=>setHov(null)}
                      onClick={()=>handleOpcion(o)}/>
                  </Grid>
                ))}
              </Grid>
          </Grid>

          {/* ── Col derecha ── */}
          <Grid item xs={12} lg={4} className="integra-launcher-cards caja-widgets" component="aside" aria-label="Resumen del módulo">

            <Box className={'cjm-widget cjm-session '+(cajaInfo?'is-open':'')}>
              <div className="cjm-widget-head"><span className="cjm-kicker">Tu caja</span><span className="cjm-state"><i/>{cargCaja?'Consultando':cajaInfo?'Abierta':'Cerrada'}</span></div>
              {cargCaja ? <Box className="cjm-loading"><CircularProgress size={24}/></Box> : cajaInfo ? <>
                <div className="cjm-session-title">{cajaInfo.cajaNombre||'Caja en atención'}</div><span className="cjm-session-id" title={cajaInfo.idSesion||''}>Sesión #{cajaInfo.idSesion||'—'}</span>
                <div className="cjm-session-bottom"><div><span>Apertura</span><strong>{cajaInfo.fechaApertura||'—'}</strong></div><div><span>Hora de inicio</span><strong>{cajaInfo.horaApertura||'—'}</strong></div></div>
              </> : <><div className="cjm-session-title">Lista para una nueva jornada</div><p className="cjm-description">Abre la caja para empezar a registrar tus ventas.</p><button className="cjm-primary" onClick={()=>setModalApertura(true)}>Abrir caja <span aria-hidden="true">↗</span></button></>}
            </Box>
            <Box className="cjm-widget cjm-balance">
              <div className="cjm-widget-head"><span className="cjm-kicker">Control de caja</span><span className="cjm-count">{totalSesion.cantidad} ventas</span></div>
              <div className="cjm-money-label">Efectivo de la sesión</div><div className="cjm-amounts"><strong className="cjm-money">{fmtS(totalSesion.suma)}</strong>
              
              </div><button className="cjm-close-session" onClick={cerrarCaja} disabled={cerrando||!cajaInfo}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V6a4 4 0 0 1 8 0v4"/></svg>{cerrando?'Cerrando…':'Cierre de caja'}</button>
            </Box>
            <Box className="cjm-widget cjm-chart cjm-activity">
              <div className="cjm-widget-head"><div><h2>Actividad de caja</h2><p>{actividadVista==='sesiones'?'Ventas por sesión':'Anulaciones de los últimos 6 meses'}</p></div><span className={'cjm-chart-symbol '+(actividadVista==='sesiones'?'green':'rose')} aria-hidden="true">{actividadVista==='sesiones'?'↗':'↶'}</span></div>
              <div className={'cjm-activity-switch '+(actividadVista==='anulaciones'?'show-cancellations':'')} role="tablist" aria-label="Actividad de caja">
                <span className="cjm-activity-slider" aria-hidden="true"/>
                {['sesiones','anulaciones'].map((vista,i)=><button key={vista} id={'cjm-tab-'+vista} type="button" role="tab" aria-selected={actividadVista===vista} aria-controls="cjm-activity-panel" tabIndex={actividadVista===vista?0:-1} onClick={()=>setActividadVista(vista)} onKeyDown={e=>{if(['ArrowLeft','ArrowRight','Home','End'].includes(e.key)){e.preventDefault();const next=e.key==='Home'?'sesiones':e.key==='End'?'anulaciones':i===0?'anulaciones':'sesiones';setActividadVista(next);document.getElementById('cjm-tab-'+next)?.focus();}}}>{i===0?'Mis sesiones':'Anulaciones'}</button>)}
              </div>
              <div id="cjm-activity-panel" role="tabpanel" aria-labelledby={'cjm-tab-'+actividadVista} className="cjm-chart-canvas" key={actividadVista}>
                {cargGraf?<Box className="cjm-loading"><CircularProgress size={20}/></Box>:actividadVista==='sesiones'?(sesiones.length?<GraficoSesiones data={sesiones}/>:<div className="cjm-loading">Aún no hay sesiones</div>):(anulaciones.length?<GraficoAnulaciones data={anulaciones}/>:<div className="cjm-loading">Sin anulaciones registradas</div>)}
              </div>
            </Box>
            {(cpesPendientes.length>0||enviandoAuto)&&<Box component="button" className="cjm-widget cjm-pending" disabled={enviandoAuto} onClick={()=>!enviandoAuto&&cpesPendientes.length>0&&setModalPendientes(true)}>
              <div className="cjm-widget-head"><span className="cjm-kicker">Comprobantes · SUNAT</span>{enviandoAuto?<CircularProgress size={18}/>:<span className="cjm-state">Pendientes</span>}</div>
              <strong className="cjm-pending-number">{cpesPendientes.length}</strong><h2>{enviandoAuto?'Enviando a SUNAT…':'Comprobantes por enviar'}</h2><p>{enviandoAuto?'Reenvío automático en curso':'Revisa los pendientes y reintenta su envío.'}</p><span className="cjm-pending-link">{enviandoAuto?'Procesando':'Revisar comprobantes →'}</span>
            </Box>}

          </Grid>

          {modalPendientes && (
            <Box sx={{position:'fixed',inset:0,bgcolor:'rgba(0,0,0,0.5)',zIndex:9999,
              display:'flex',alignItems:'center',justifyContent:'center',
              backdropFilter:'blur(6px)',px:2}}>
              <Box sx={{bgcolor:IOS.surface,borderRadius:'20px',width:'100%',maxWidth:420,
                maxHeight:'75vh',display:'flex',flexDirection:'column',
                boxShadow:'0 24px 60px rgba(0,0,0,0.25)',overflow:'hidden'}}>

                {/* Header */}
                <Box sx={{px:2.5,pt:2.5,pb:2,borderBottom:`1px solid ${IOS.separator}`,
                  display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                  <Box sx={{display:'flex',alignItems:'center',gap:1.2}}>
                    <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${IOS.orange}12`,
                      display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </Box>
                    <Box>
                      <Typography sx={{fontWeight:700,fontSize:'0.92rem',color:IOS.label}}>CPEs pendientes de envío</Typography>
                      <Typography sx={{fontSize: '0.72rem',color:IOS.label2}}>{cpesPendientes.length} comprobante{cpesPendientes.length>1?'s':''} sin enviar a SUNAT</Typography>
                    </Box>
                  </Box>
                  <Box component="button" onClick={()=>setModalPendientes(false)}
                    sx={{width:28,height:28,borderRadius:'50%',border:'none',bgcolor:'#E5E5EA',
                      cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </Box>
                </Box>

                {/* Lista simple sin scroll lento */}
                <Box sx={{flex:1,overflowY:'auto',px:2,py:1}}>
                  {cpesPendientes.map((p,i)=>(
                    <Box key={p.idVenta||i} sx={{display:'flex',alignItems:'center',gap:1.2,
                      py:1,borderBottom:i<cpesPendientes.length-1?`1px solid ${IOS.separator}`:'none'}}>
                      <Box sx={{width:28,height:28,borderRadius:'8px',bgcolor:`${IOS.orange}10`,
                        display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <Typography sx={{fontSize:'0.7rem',fontWeight:800,color:IOS.orange}}>
                          {(p.tipoComprobante||'').charAt(0)}
                        </Typography>
                      </Box>
                      <Box sx={{flex:1,minWidth:0}}>
                        <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:IOS.label}}>{p.comprobante}</Typography>
                        <Typography sx={{fontSize: '0.72rem',color:IOS.label2}}>{p.fecha} · S/{parseFloat(p.total||0).toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{px:0.8,py:0.3,borderRadius:'20px',bgcolor:`${IOS.orange}10`,border:`1px solid ${IOS.orange}30`}}>
                        <Typography sx={{fontSize: '0.72rem',fontWeight:700,color:IOS.orange}}>PENDIENTE</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Footer — solo reenviar todos */}
                <Box sx={{px:2.5,py:2,borderTop:`1px solid ${IOS.separator}`,flexShrink:0,display:'flex',flexDirection:'column',gap:1}}>
                  {reenvioResultado&&(
                    <Box sx={{p:1.2,borderRadius:'10px',
                      bgcolor:reenvioResultado.fallidos===0?'#EDFAF2':'#FFF8E6',
                      border:`1px solid ${reenvioResultado.fallidos===0?'#B7EFD0':'#FFE9A0'}`}}>
                      <Typography sx={{fontSize:'0.75rem',fontWeight:600,
                        color:reenvioResultado.fallidos===0?'#1A6B3C':'#8B6914'}}>
                        {reenvioResultado.fallidos===0
                          ?`✓ ${reenvioResultado.reenviados} comprobante${reenvioResultado.reenviados>1?'s':''} enviado${reenvioResultado.reenviados>1?'s':''} a SUNAT`
                          :`${reenvioResultado.reenviados} enviados · ${reenvioResultado.fallidos} con error`}
                      </Typography>
                      {reenvioResultado.message&&<Typography sx={{fontSize:12,mt:1,color:'#8B6914',overflowWrap:'anywhere'}}>{reenvioResultado.message}</Typography>}
                      {reenvioResultado.detalle?.filter(d=>!d.success).map(d=><Box key={d.idVenta} sx={{mt:1,pt:1,borderTop:'1px solid #EAD9BB'}}><Typography sx={{fontSize:12,fontWeight:650,color:'#704C27'}}>{cpesPendientes.find(p=>p.idVenta===d.idVenta)?.comprobante||('Venta '+d.idVenta)}</Typography><Typography sx={{fontSize:12,color:'#805B38',whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{d.message||'Envío no confirmado.'}</Typography></Box>)}
                    </Box>
                  )}
                  <Box component="button" onClick={reenviarTodos} disabled={enviandoTodos||cpesPendientes.length===0}
                    sx={{width:'100%',height:42,borderRadius:'12px',border:'none',
                      bgcolor:enviandoTodos||cpesPendientes.length===0?'#E5E5EA':IOS.blue,
                      cursor:enviandoTodos||cpesPendientes.length===0?'default':'pointer',
                      display:'flex',alignItems:'center',justifyContent:'center',gap:1,
                      boxShadow:!enviandoTodos&&cpesPendientes.length>0?`0 4px 12px ${IOS.blue}30`:'none'}}>
                    {enviandoTodos&&<CircularProgress size={15} sx={{color:'white'}}/>}
                    <Typography sx={{fontSize:'0.85rem',fontWeight:700,
                      color:enviandoTodos||cpesPendientes.length===0?'#8E8E93':'white'}}>
                      {enviandoTodos?'Reenviando a SUNAT...':`Reenviar todos (${cpesPendientes.length})`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          
        </Grid>
      </Box>

      <ModalApertura 
        open={modalApertura} 
        onClose={()=>setModalApertura(false)}
        onAbierta={()=>{ cargarEstado(); }} 
        idUe={ueId}   // ← cambio clave
      />
      <ModalSinSesion open={modalSinSesion} onClose={()=>setModalSinSesion(false)}
        onAperturar={()=>{ setModalSinSesion(false); setModalApertura(true); }} />
      <style>{`
.caja-widgets .cjm-widget{box-sizing:border-box;min-width:0;border:1px solid #e1e7ee;border-radius:22px;padding:22px;margin-bottom:16px;background:#fff;box-shadow:0 8px 24px rgba(35,58,84,.055);display:flex;flex-direction:column;text-align:left}
.cjm-widget-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:18px}
.cjm-kicker{font-size:11px;font-weight:650;color:#7f8b9d;letter-spacing:.02em}
.cjm-state{display:inline-flex;align-items:center;gap:6px;padding:5px 9px;border-radius:8px;background:#eef3f5;color:#758593;font-size:10px;font-weight:600}
.cjm-state i{width:5px;height:5px;background:currentColor;border-radius:50%}
.caja-widgets .cjm-session.is-open{background:linear-gradient(120deg,#258867,#2b9373);border-color:#2a8d70;color:#fff;box-shadow:0 10px 26px #278b7026,inset 0 1px 0 #ffffff25}
.cjm-session.is-open .cjm-kicker{color:#d1eee3}.cjm-session.is-open .cjm-state{background:#ffffff21;color:#e6fff3}
.cjm-session-title{font-size:23px;line-height:1.25;font-weight:650;letter-spacing:-.5px;overflow-wrap:anywhere}
.cjm-session-id{font-size:11px;opacity:.7;margin-top:6px}
.cjm-session-bottom{display:grid;grid-template-columns:1.2fr 1fr;gap:16px;margin-top:24px;padding-top:16px;border-top:1px solid #ffffff30}
.cjm-session-bottom span{display:block;font-size:10px;opacity:.7;margin-bottom:5px}.cjm-session-bottom strong{font-size:13px;font-weight:600}
.cjm-description{font-size:12px;color:#8290a1;line-height:1.6}
.cjm-primary,.cjm-close-session{display:flex;align-items:center;justify-content:center;gap:8px;min-height:40px;width:100%;padding:10px 14px;border-radius:10px;border:0;font:inherit;font-size:12px;font-weight:600;cursor:pointer}
.cjm-primary{margin-top:auto;background:#288f70;color:white;justify-content:space-between}
.cjm-count{color:#6387b0;background:#edf3fb;padding:5px 9px;border-radius:8px;font-size:10px;font-weight:600}
.cjm-money-label{font-size:11px;color:#8491a3;margin-bottom:7px}.cjm-money{font-size:30px;letter-spacing:-1px;font-weight:650;color:#2b3d55;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}.cjm-money-usd{color:#637e9c;font-size:16px;margin-top:6px}
.cjm-close-session{margin-top:22px;color:#b06372;background:#fcf0f3;border:1px solid #f4dee4}.cjm-close-session:hover:not(:disabled){background:#f8e6eb}.cjm-close-session:disabled{background:#f4f5f7;color:#a5adba;border-color:#edf0f4;cursor:default}
.cjm-widget h2{margin:0;color:#34445c;font-size:14px;font-weight:650}.cjm-widget p{font-size:11px;color:#8895a6;line-height:1.6;margin:5px 0 0}
.cjm-chart-symbol{width:32px;height:32px;display:grid;place-items:center;border-radius:10px;font-size:20px;flex-shrink:0}.cjm-chart-symbol.green{background:#edf7f2;color:#549d7f}.cjm-chart-symbol.rose{background:#fbf0f2;color:#bc7b88}
.cjm-chart-canvas{height:140px;position:relative;margin-top:2px}.cjm-loading{height:100%;min-height:100px;display:flex;align-items:center;justify-content:center;color:#929eae;font-size:12px}
.caja-widgets .cjm-pending{font-family:inherit;background:#fffaf2;border-color:#f0e2c9;cursor:pointer;width:100%;margin-top:0}.cjm-pending:disabled{cursor:default}.cjm-pending .cjm-state{background:#fbedcf;color:#b18a42}.cjm-pending-number{font-size:38px;color:#bd8b38;font-weight:650;line-height:1;margin-bottom:12px}.cjm-pending-link{margin-top:18px;font-size:12px;font-weight:600;color:#b18437}
.caja-menu :is(.cjm-primary,.cjm-close-session,.cjm-pending):focus-visible{outline:3px solid #479be5;outline-offset:3px}
@media(max-width:1199px){.caja-menu .caja-widgets>.cjm-widget{min-height:280px;padding:24px;display:flex;flex-direction:column}.cjm-session-bottom{margin-top:auto}.cjm-balance .cjm-close-session{margin-top:auto}.cjm-chart .cjm-chart-canvas{flex:1;min-height:150px}.cjm-pending-link{margin-top:auto}.cjm-pending h2{margin-top:6px}}

.caja-widgets .cjm-session.is-open{padding:18px 20px}
.cjm-session.is-open .cjm-widget-head{margin-bottom:11px}
.cjm-session.is-open .cjm-session-title{font-size:20px;letter-spacing:-.3px}
.cjm-session.is-open .cjm-session-id{font-size:10px;margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
.cjm-session.is-open .cjm-session-bottom{margin-top:14px;padding-top:11px;gap:12px}
.cjm-session.is-open .cjm-session-bottom>div{display:flex;align-items:baseline;gap:7px;flex-wrap:wrap}
.cjm-session.is-open .cjm-session-bottom span{margin:0;font-size:10px}
.cjm-session.is-open .cjm-session-bottom strong{font-size:12px}
@media(max-width:1199px){.caja-menu .caja-widgets>.cjm-session.is-open{padding:20px}.cjm-session.is-open .cjm-session-bottom{margin-top:auto}}


.caja-widgets .cjm-balance{padding:18px 20px}
.cjm-balance .cjm-widget-head{margin-bottom:12px}
.cjm-balance .cjm-money-label{margin-bottom:5px;font-size:10px}
.cjm-amounts{display:flex;align-items:baseline;flex-wrap:wrap;gap:6px 12px}
.cjm-balance .cjm-money{font-size:27px;letter-spacing:-.7px}
.cjm-balance .cjm-money-usd{font-size:14px;margin:0}
.cjm-balance .cjm-close-session{margin-top:14px;background:#dc5065;color:#fff;border-color:#dc5065;min-height:38px;padding:8px 12px;box-shadow:0 4px 10px #dc50651a}
.cjm-balance .cjm-close-session:hover:not(:disabled){background:#ca4055;border-color:#ca4055}
.cjm-balance .cjm-close-session:disabled{background:#f4f5f7;color:#a5adba;border-color:#edf0f4;box-shadow:none}
.cjm-activity-switch{position:relative;display:grid;grid-template-columns:1fr 1fr;padding:4px;background:#eaf0f5;border:1px solid #e0e6ee;border-radius:12px;margin-bottom:16px;isolation:isolate}
.cjm-activity-slider{position:absolute;top:4px;bottom:4px;left:4px;width:calc(50% - 4px);background:white;border-radius:8px;box-shadow:0 1px 4px #25355214;transition:transform .22s ease;z-index:-1}
.cjm-activity-switch.show-cancellations .cjm-activity-slider{transform:translateX(100%)}
.cjm-activity-switch button{border:0;background:transparent;color:#8290a3;min-height:32px;font:inherit;font-size:11px;font-weight:600;cursor:pointer;border-radius:8px}
.cjm-activity-switch button[aria-selected="true"]{color:#344b65}
.cjm-activity-switch button:focus-visible{outline:2px solid #528de1;outline-offset:1px}
.cjm-activity .cjm-widget-head{margin-bottom:14px}
@media(max-width:1199px){.caja-menu .caja-widgets>.cjm-balance{padding:20px}.cjm-balance .cjm-close-session{margin-top:auto}}

@media(prefers-reduced-motion:reduce){.caja-widgets *{animation:none!important}}
`}</style>
    </Box>
  );
};

export default Caja;
